import React, { useState } from 'react'
import { MessageSquare, Plus, Trash2, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import styles from './ConversationHistorySidebar.module.scss'
import { ConversationSession } from '../../../../../utils/conversationStorage.ts'
import Logo from '../../../../../assets/images/Logo-edify-no-bg.png'
import LogoCutted from '../../../../../assets/images/LogoEdifyNavCutted.png'

interface ConversationHistorySidebarProps {
  sessions: ConversationSession[]
  currentSessionId: string | null
  onSelectSession: (sessionId: string) => void
  onCreateNewSession: () => void
  onDeleteSession: (sessionId: string) => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  userId?: string
  userData?: any
  onOpenSearchModal?: () => void
}

const ConversationHistorySidebar: React.FC<ConversationHistorySidebarProps> = ({
  sessions,
  currentSessionId,
  onSelectSession,
  onCreateNewSession,
  onDeleteSession,
  isCollapsed = false,
  onToggleCollapse,
  userId,
  userData,
  onOpenSearchModal
}) => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  const handleOpenSearch = () => {
    setIsSearchModalOpen(true)
    onOpenSearchModal?.()
  }

  const getPreview = (session: ConversationSession) => {
    const firstUserMessage = session.messages.find(m => m.role === 'user')
    if (firstUserMessage) {
      return firstUserMessage.content.length > 50
        ? firstUserMessage.content.substring(0, 50) + '...'
        : firstUserMessage.content
    }
    return 'Nueva conversación'
  }

  return (
    <>
      <div className={`${styles.historySidebar} ${isCollapsed ? styles.historySidebarCollapsed : ''}`}>
        <div className={styles.sidebarHeader}>
          {isCollapsed ? (
            <div
              className={styles.logoContainerSidebarCollapsed}
              onClick={() => window.location.href = '/dashboard'}
            >
              <img src={LogoCutted} alt="Logo Edify" className={styles.logoCuttedSidebar} />
              {onToggleCollapse && (
                <button
                  className={styles.expandButton}
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleCollapse()
                  }}
                  type="button"
                  aria-label="Expandir sidebar"
                >
                  <ChevronRight size={20} />
                </button>
              )}
            </div>
          ) : (
            <div
              className={styles.logoContainerSidebar}
              onClick={() => window.location.href = '/dashboard'}
            >
              <img src={Logo} alt="Logo Edify" className={styles.logoSidebar} />
              {onToggleCollapse && (
                <button
                  className={styles.collapseButton}
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleCollapse()
                  }}
                  type="button"
                  aria-label="Colapsar sidebar"
                >
                  <ChevronLeft size={20} />
                </button>
              )}
            </div>
          )}
        </div>

        {isCollapsed ? (
          <div className={styles.collapsedActions}>
            <button
              className={styles.collapsedActionButton}
              onClick={onCreateNewSession}
              type="button"
              aria-label="Nueva conversación"
              title="Nueva conversación"
            >
              <Plus size={20} />
            </button>
            <button
              className={styles.collapsedActionButton}
              onClick={handleOpenSearch}
              type="button"
              aria-label="Buscar chats"
              title="Buscar chats"
            >
              <Search size={20} />
            </button>
          </div>
        ) : (
          <div className={styles.sessionsList}>
            <button
              className={styles.newChatButtonItem}
              onClick={onCreateNewSession}
              type="button"
              aria-label="Nueva conversación"
            >
              <Plus size={18} />
              <span>Nueva conversación</span>
            </button>

            {onOpenSearchModal && (
              <button
                className={styles.searchChatButtonItem}
                onClick={handleOpenSearch}
                type="button"
                aria-label="Buscar chats"
              >
                <Search size={18} />
                <span>Buscar chats</span>
              </button>
            )}

            <div className={styles.historySection}>
              <h3 className={styles.historyTitle}>Historial</h3>
              {sessions.length === 0 ? (
                <div className={styles.emptySessions}>
                  <MessageSquare size={32} className={styles.emptyIcon} />
                  <p className={styles.emptyText}>No hay conversaciones aún</p>
                </div>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.sessionId}
                    className={`${styles.sessionItem} ${
                      session.sessionId === currentSessionId ? styles.sessionItemActive : ''
                    }`}
                    onClick={() => onSelectSession(session.sessionId)}
                  >
                    <div className={styles.sessionContent}>
                      <div className={styles.sessionPreview}>
                        <span className={styles.sessionText}>{getPreview(session)}</span>
                      </div>
                    </div>
                    <button
                      className={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeleteSession(session.sessionId)
                      }}
                      type="button"
                      aria-label="Eliminar conversación"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ConversationHistorySidebar
